import { getUrlWithVariant } from '@shopify/theme-product-form';
import isMobile from 'is-mobile';
import { getVariantFromId } from '@shopify/theme-product';
import { slideDown, slideStop, slideUp } from 'slide-anim';

import {
  add,
  listen,
  qs,
  qsa,
  remove,
  toggle,
  contains,
} from '@fluorescent/dom';
import { cart, getProduct, section } from '@/glow';
import { emit, on } from '@/glow/events';

import accordions from '@/lib/accordions';
import Lightbox from '@/lib/lightbox';
import Media from '@/lib/media';
import ProductForm from '@/lib/product-form';
import { switchImage } from '@/utils';
import ShippingEstimator from '@/lib/shipping-estimator';
import {
  quantityInput,
  variantPopup,
  updatePrices,
  updateSku,
  updateBuyButton,
  quickPurchaseBar,
  reviewsHandler,
  badgeHandler,
  optionButtons,
} from '@/lib/product';
import { updateUnitPrices } from '@/lib/unit-pricing';
import storeAvailability from '@/lib/store-availability';
import { wrapIframes, wrapTables } from '@/lib/rte';

const selectors = {
  form: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  variantSelect: '[data-variant-select]',
  optionById: id => `[value='${id}']`,
  thumb: '[data-product-thumbnail]',
  shippingEstimatorButton: '[data-estimator-trigger]',
  storeAvailability: '[data-store-availability-container]',
  quantityError: '[data-quantity-error]',
};

section('product', {
  productForm: null,
  reviewsHandler: null,
  accordions: [],

  onLoad() {
    const { productHasOnlyDefaultVariant } = this.container.dataset;

    this.formElement = qs(selectors.form, this.container);

    this.images = qsa('[data-open]', this.container);
    this.quantityError = qs(selectors.quantityError, this.container);

    const viewInYourSpace = qs('[data-in-your-space]', this.container);
    viewInYourSpace && toggle(viewInYourSpace, 'visible', isMobile());

    this.productThumbs = qsa(selectors.thumb, this.container);

    // Handle Surface pickup
    this.storeAvailabilityContainer = qs(
      selectors.storeAvailability,
      this.container
    );
    this.availability = null;

    window.SPRCallbacks = {};

    window.SPRCallbacks.onReviewsLoad = () => {
      if (!this.reviewsHandler) {
        this.reviewsHandler = reviewsHandler();
      }
    };

    window.SPRCallbacks.onBadgeLoad = () => {
      this.badgeHandler = badgeHandler();
    };

    window.SPRCallbacks.onFormFailure = () => {
      emit('spr-form:updated');
    };

    window.SPRCallbacks.onFormSuccess = () => {
      emit('spr-form:updated');
    };

    if (this.formElement) {
      const { productHandle, currentProductId } = this.formElement.dataset;
      const product = getProduct(productHandle);
      product(data => {
        const variant = getVariantFromId(data, parseInt(currentProductId));

        if (this.storeAvailabilityContainer && variant) {
          this.availability = storeAvailability(
            this.storeAvailabilityContainer,
            data,
            variant,
            { hideVariantTitle: productHasOnlyDefaultVariant === 'true' }
          );
        }

        const quickPurchaseBarEnabled = qs(
          '[data-quick-purchase-bar]',
          this.container
        );

        if (quickPurchaseBarEnabled) {
          this.quickPurchaseBar = quickPurchaseBar(this.container, variant);
        }

        this.productForm = ProductForm(this.container, this.formElement, data, {
          onOptionChange: e => this.onOptionChange(e),
          onFormSubmit: e => this.onFormSubmit(e),
          onQuantityChange: e => this.onQuantityChange(e),
        });
      });
    }

    this.quantityInput = quantityInput(this.container);

    this.socialButtons = qsa('[data-social-share]', this.container);

    const accordionElements = qsa('.accordion', this.container);
    accordionElements.forEach(accordion => {
      const accordionOpen = accordion.classList.contains('accordion--open');
      this.accordions.push(accordions(accordion, { firstOpen: accordionOpen }));

      const accordionParent = accordion.parentElement;
      if (
        accordionParent.classList.contains('rte--product') &&
        !accordionParent.classList.contains('accordion accordion--product')
      ) {
        accordion.classList.add('rte--product', 'accordion--product');
      }
    });

    this.lightbox = Lightbox(qs('[data-lightbox]', this.container));
    this.media = Media(qs('.product__media-container', this.container));
    this.optionButtons = optionButtons(
      qsa('[data-option-buttons]', this.container)
    );

    this.variantPopup = variantPopup(this.container);

    this.shippingEstimatorButtons = qsa(
      selectors.shippingEstimatorButton,
      this.container
    );

    this.shippingEstimator = this.shippingEstimatorButtons.map(button =>
      ShippingEstimator(button.parentNode)
    );

    const productDescriptionWrapper = qs(
      '.product__description',
      this.container
    );

    if (productDescriptionWrapper) {
      wrapIframes(qsa('iframe', productDescriptionWrapper));
      wrapTables(qsa('table', productDescriptionWrapper));
    }

    this._initEvents();
  },

  _initEvents() {
    this.events = [
      listen(this.productThumbs, 'click', e => {
        e.preventDefault();
        const {
          currentTarget: { dataset },
        } = e;

        this.productThumbs.forEach(thumb => remove(thumb, 'active'));
        add(e.currentTarget, 'active');

        switchImage(this.container, dataset.thumbnailId);
      }),
      listen(this.images, 'click', e => {
        e.preventDefault();

        this.lightbox.open(e.currentTarget.dataset.open);
      }),
      listen(this.socialButtons, 'click', e => {
        toggle(e.target, 'active');
        const sub = qs('.article__share-icons', e.target);

        sub.setAttribute('aria-hidden', !contains(e.target, 'active'));
      }),
    ];
  },

  // When the user changes a product option
  onOptionChange({ dataset: { variant } }) {
    const buyButton = qs(selectors.addToCart, this.container);

    // Update prices to reflect selected variant
    updatePrices(this.container, variant);

    // Update buy button
    updateBuyButton(buyButton, variant);

    // Update unit pricing
    updateUnitPrices(this.container, variant);

    // Update sku
    updateSku(this.container, variant);

    if (!variant) {
      updateBuyButton(qs('[data-add-to-cart]', this.container), false);
      this.shippingEstimatorButtons.forEach(btn => add(btn, 'hidden'));

      this.availability && this.availability.unload();
      return;
    }

    this.quickPurchaseBar && this.quickPurchaseBar.update(variant);

    // Update URL with selected variant
    const url = getUrlWithVariant(window.location.href, variant.id);
    window.history.replaceState({ path: url }, '', url);

    // We need to set the id input manually so the Dynamic Checkout Button works
    const selectedVariantOpt = qs(
      `${selectors.variantSelect} ${selectors.optionById(variant.id)}`,
      this.container
    );
    selectedVariantOpt.selected = true;

    // We need to dispatch an event so Shopify pay knows the form has changed
    this.formElement.dispatchEvent(new Event('change'));

    // Update selected variant image and thumb
    if (variant.featured_media) {
      switchImage(this.container, variant.featured_media.id);

      const thumb = qs(
        `[data-thumbnail-id="${variant.featured_media.id}"]`,
        this.container
      );

      this.productThumbs.forEach(thumb => remove(thumb, 'active'));
      add(thumb, 'active');
    }

    // Update product availability content
    this.availability && this.availability.update(variant);

    this.shippingEstimatorButtons.forEach(btn => remove(btn, 'hidden'));
  },

  // When user updates quantity
  onQuantityChange({ dataset: { variant, quantity } }) {
    // Adjust the hidden quantity input within the form
    const quantityInputs = [...qsa('[name="quantity"]', this.formElement)];
    quantityInputs.forEach(quantityInput => {
      quantityInput.value = quantity;
    });

    if (!variant) return;
    this.quickPurchaseBar && this.quickPurchaseBar.update(variant);
  },

  // When user submits the product form
  onFormSubmit(e) {
    const { enableCartAjax } = document.body.dataset;
    if (!enableCartAjax) return;

    e.preventDefault();

    add(this.quantityError, 'hidden');

    const button = qs(selectors.addToCart, this.container);
    add(button, 'loading');
    cart
      .addItem(this.formElement)
      .then(({ item }) => {
        remove(button, 'loading');
        emit('cart:open', null, { flash: item.variant_id });
      })
      .catch(() => {
        remove(this.quantityError, 'hidden');
        const button = qs(selectors.addToCart, this.container);
        remove(button, 'loading');
      });
  },

  onBlockSelect({ target }) {
    const label = qs('.accordion__label', target);
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });

    if (!label) return;
    const { parentNode: group, nextElementSibling: content } = label;

    slideStop(content);
    slideDown(content);
    group.setAttribute('data-open', true);
    label.setAttribute('aria-expanded', true);
    content.setAttribute('aria-hidden', false);
  },

  onBlockDeselect({ target }) {
    const label = qs('.accordion__label', target);
    if (!label) return;
    const { parentNode: group, nextElementSibling: content } = label;

    slideStop(content);
    slideUp(content);
    group.setAttribute('data-open', false);
    label.setAttribute('aria-expanded', false);
    content.setAttribute('aria-hidden', true);
  },

  onUnload() {
    this.productForm.destroy();
    this.lightbox.destroy();
    this.shippingEstimator.forEach(unsubscribe => unsubscribe());
    this.accordions.forEach(accordion => accordion.unload());
    this.optionButtons.destroy();
    this.quantityInput.unload();
    this.events.forEach(unsubscribe => unsubscribe());
    this.quickPurchaseBar && this.quickPurchaseBar.unload();
    this.reviewsHandler && this.reviewsHandler.unload();
  },
});
